import type { MetaItem } from './MetaStrip';
import { hasLiveValue, type StudentPlanData } from './planData';
import { elFlagSuffix, ltelBannerYears, type PlanStudent } from './derive';
import type { PlanIep, PlanStanding } from './planRecordApi';

export { ltelBannerYears };

export function schoolName(student: PlanStudent | null, live: StudentPlanData | null): string {
  return student?.latestSchool || (hasLiveValue(live?.school.name) ? live.school.name : '');
}

export function gradeText(student: PlanStudent | null, live: StudentPlanData | null): string {
  const grade = student?.latestGrade || (hasLiveValue(live?.student.grade) ? live.student.grade : '');
  if (!grade) return '';
  return grade.startsWith('Grade') ? grade : `Grade ${grade}`;
}

export function elTeacherName(student: PlanStudent | null, live: StudentPlanData | null): string {
  return student?.sis?.elTeacher ?? (hasLiveValue(live?.student.elTeacher) ? live.student.elTeacher : '');
}

export function displayElStatus(status: string): string {
  return status.trim().toLowerCase() === 'reclassified' ? 'Former EL' : status;
}

const STANDING_LABELS: Record<PlanStanding, string> = { active: 'Active', monitoring: 'Monitoring', former: 'Former EL', waived: 'Waived', departed: 'Departed', unknown: 'Unknown' };

function statusSuffix(student: PlanStudent): string {
  if (student.tenure) {
    if (student.tenure.ltel.isLtel) return ' · LTEL';
    if (student.tenure.newcomer.isNewcomer) return ' · Newcomer';
    return '';
  }
  return elFlagSuffix(student.yearsInEl, student.thresholds);
}

export function iepFieldValue(iep: PlanIep | null | undefined): string {
  if (!iep) return '';
  if (iep.sis === true && iep.section504 === true) return 'IEP and Section 504 on file · from the SIS';
  if (iep.sis === true) return 'IEP on file · from the SIS';
  if (iep.section504 === true) return 'Section 504 on file · from the SIS';
  return '';
}

export function sisFieldsMissing(student: PlanStudent | null, live: StudentPlanData | null): boolean {
  if (student?.sis) return false;
  const liveFields = [live?.student.homeLanguage, live?.student.yearsInEl, live?.student.ellStatus, live?.student.programModel];
  if (liveFields.some(hasLiveValue)) return false;
  return !student?.nativeLanguage && student?.yearsInEl == null;
}

export function metaItems(student: PlanStudent | null, live: StudentPlanData | null, iep?: PlanIep | null, standing?: PlanStanding | null): MetaItem[] {
  const yearsInEl = student?.yearsInEl != null
    ? `${student.yearsInEl} year${student.yearsInEl === 1 ? '' : 's'}${statusSuffix(student)}`
    : hasLiveValue(live?.student.yearsInEl)
      ? live.student.yearsInEl
      : '';
  const homeLanguage = student?.nativeLanguage || (hasLiveValue(live?.student.homeLanguage) ? live.student.homeLanguage : '');
  const rawStatus = student?.sis?.elStatus
    ?? (hasLiveValue(live?.student.ellStatus) ? live.student.ellStatus : student ? 'Unknown' : '');
  const status = standing ? STANDING_LABELS[standing] : displayElStatus(rawStatus);
  const programModel = student?.sis?.programModel
    ?? (hasLiveValue(live?.student.programModel) ? live.student.programModel : '');
  const iepField = iepFieldValue(iep);
  return [
    { label: 'Home Language', value: homeLanguage },
    { label: 'Years in EL', value: yearsInEl },
    { label: 'Status', value: status, pill: true },
    ...(iepField ? [{ label: 'IEP / 504', value: iepField }] : []),
    { label: 'Program Model', value: programModel },
    { label: 'EL Teacher', value: elTeacherName(student, live) },
  ];
}
